import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    CAlert,
    CBadge,
    CButton,
    CSpinner
} from '@coreui/react';
import { getApi } from '../../apis/backendApis';
import FlowGraph from './flowGraph';
import StepModal from './stepModal';
import ReverterModal from './reverterModal';
import {
    InfraGrafo,
    InfraNo,
    InfraProducaoHistorico,
    SecaoDoRamo,
    STEP_EXECUTANDO,
    TIPO_PRODUCAO,
    linhaDoRamo,
    corSituacaoPipeline,
    descricaoSituacaoPipeline,
    rotuloDeploy
} from '../../models/InfraPipeline';
import '../../scss/styles-infra.scss';

const PipelineFlow = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const [grafo, setGrafo] = useState<InfraGrafo | null>(null);
    const [selecionado, setSelecionado] = useState<string | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [executando, setExecutando] = useState<string | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);
    const [historico, setHistorico] = useState<InfraProducaoHistorico | null>(null);
    const [mostrandoHistorico, setMostrandoHistorico] = useState<boolean>(false);
    const [carregandoHistorico, setCarregandoHistorico] = useState<boolean>(false);
    const [confirmandoNovo, setConfirmandoNovo] = useState<boolean>(false);
    const [trocandoDeploy, setTrocandoDeploy] = useState<boolean>(false);
    const [acompanhando, setAcompanhando] = useState<Array<string>>([]);
    const [versaoRuns, setVersaoRuns] = useState<number>(0);
    // Sobe a cada clique em Executar. O detalhe do passo usa isso para trocar
    // de tentativa na hora, sem esperar a primeira volta do polling.
    const [execucaoIniciada, setExecucaoIniciada] = useState<number>(0);
    const [reverter, setReverter] = useState<InfraNo | null>(null);
    const [erroReversao, setErroReversao] = useState<string | null>(null);
    const [salvandoRamos, setSalvandoRamos] = useState<boolean>(false);
    const [revertendo, setRevertendo] = useState<boolean>(false);
    const timerRef = useRef<number | null>(null);

    const carregar = useCallback(() => {
        setCarregando(true);
        getApi().get('/infra/pipeline/get/' + id)
            .then(resposta => {
                setGrafo(resposta.data.grafo);
                setErro(null);
            })
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui carregar o pipeline.');
            })
            .finally(() => setCarregando(false));
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    // Recado deixado pela tela anterior ao abandonar um deploy: quais passos
    // continuaram rodando fora do admin. Ele nasce lá e é lido aqui porque a
    // navegação acontece no mesmo clique, e um alerta na tela que se fecha
    // ninguém leria. Consome de uma vez para não reaparecer ao recarregar.
    useEffect(() => {
        try {
            const recado = window.sessionStorage.getItem('infra-aviso-abandono');
            if (recado) {
                window.sessionStorage.removeItem('infra-aviso-abandono');
                setAviso(recado);
            }
        } catch (e) {
            // Navegador com armazenamento bloqueado: o recado é dispensável.
        }
    }, []);

    /**
     * Carrega uma página do histórico de deploys de produção.
     *
     * Só faz sentido em produção: sandbox é um pipeline por cliente, e o
     * histórico dele é a própria lista da Central.
     */
    const carregarHistorico = useCallback((pagina = 1) => {
        setCarregandoHistorico(true);
        getApi().get('/infra/producao/historico', { params: { pagina } })
            .then(resposta => setHistorico({
                abertoId: resposta.data.abertoId ?? null,
                rows: resposta.data.rows || [],
                total: resposta.data.total ?? 0,
                pagina: resposta.data.pagina ?? 1,
                paginas: resposta.data.paginas ?? 1,
                porPagina: resposta.data.porPagina ?? 15
            }))
            .catch(() => setHistorico(null))
            .finally(() => setCarregandoHistorico(false));
    }, []);

    /**
     * Abre a gaveta e busca a lista na hora.
     *
     * Buscar no clique, e não ao abrir a tela: a lista guardada de quando a
     * tela abriu envelhece, e um deploy que concluiu no meio do caminho
     * continuava aparecendo como em andamento.
     */
    const abrirHistorico = () => {
        setMostrandoHistorico(true);
        // Zera antes de buscar: mostrar a lista da última vez enquanto a nova
        // não chega é exatamente o problema que a busca no clique resolve.
        setHistorico(null);
        carregarHistorico(1);
    };

    /**
     * Vai para outro deploy: mesma tela, outro id.
     *
     * A gaveta fica aberta de propósito, para dar para pular de um deploy a
     * outro comparando. Só o clique fora dela é que fecha.
     */
    const irParaDeploy = (destino: number) => {
        if (destino === Number(id)) {
            return;
        }
        setSelecionado(null);
        setErro(null);
        history.push('/infra/pipeline/' + destino);
    };

    /** Entra no deploy aberto, ou abre um se não houver nenhum. */
    const continuarDeploy = () => {
        if (trocandoDeploy) {
            return;
        }
        setTrocandoDeploy(true);
        getApi().post('/infra/producao/abrir', {})
            .then(resposta => {
                setTrocandoDeploy(false);
                setMostrandoHistorico(false);
                irParaDeploy(resposta.data.id);
            })
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui abrir o deploy de produção.');
                setTrocandoDeploy(false);
            });
    };

    /**
     * Encerra o deploy aberto e começa outro do zero.
     *
     * O anterior fica como abandonado, nem concluído nem com falha, e continua
     * consultável no histórico. Não para trabalho que já saiu daqui: se havia
     * passo rodando, o backend devolve quais e a tela avisa.
     */
    const comecarNovoDeploy = () => {
        if (trocandoDeploy) {
            return;
        }
        setTrocandoDeploy(true);
        setConfirmandoNovo(false);
        getApi().post('/infra/producao/novo', {})
            .then(resposta => {
                const rodando: Array<string> = resposta.data.executando || [];
                setTrocandoDeploy(false);
                setAviso(rodando.length > 0
                    ? 'O deploy anterior foi abandonado, mas estes passos já estavam rodando '
                        + 'fora do admin e seguem até o fim por conta própria: ' + rodando.join(', ') + '.'
                    : null);
                setMostrandoHistorico(false);
                irParaDeploy(resposta.data.id);
            })
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui começar um deploy novo.');
                setTrocandoDeploy(false);
            });
    };

    const tail = useCallback(() => {
        // Solta o relógio antes de mexer no estado, e não depois.
        //
        // O setGrafo abaixo renderiza na hora: React 17 não agrupa atualização
        // fora de evento, e o efeito que re-arma o laço roda no meio desta
        // função. Com o timer ainda marcado ali, ele via "já tem um rodando" e
        // não re-armava; a linha que zerava o timer vinha depois, e o laço
        // morria com o passo ainda rodando. Só aparecia em passo sem nada para
        // acompanhar, porque nos outros a lista de execução salvava o laço.
        timerRef.current = null;

        // Um timer por vez: este final e o efeito abaixo podem tentar reagendar
        // no mesmo instante, e dois timers viram duas consultas lendo o mesmo
        // offset. Quem chegar primeiro agenda.
        const agendar = (ms: number) => {
            if (timerRef.current === null) {
                timerRef.current = window.setTimeout(tail, ms);
            }
        };

        getApi().get('/infra/pipeline/' + id + '/tail')
            .then(resposta => {
                const emExecucao: Array<string> = resposta.data.executando || [];

                // O gráfico manda junto da lista: passo pode estar rodando sem
                // ser acompanhado por ninguém.
                const nos: Array<InfraNo> = resposta.data.grafo?.nos || [];
                const aindaRodando = emExecucao.length > 0
                    || nos.some(no => no.situacao === STEP_EXECUTANDO);

                if (resposta.data.grafo) {
                    setGrafo(resposta.data.grafo);
                }
                setAcompanhando(emExecucao);
                setVersaoRuns(v => v + 1);

                const erros = resposta.data.erros || {};
                const chavesComErro = Object.keys(erros);
                if (chavesComErro.length > 0) {
                    setErro(chavesComErro.map(c => c + ': ' + erros[c]).join(' | '));
                }

                if (aindaRodando) {
                    agendar(2000);
                }
            })
            .catch(() => {
                // Falha de rede não deve matar o acompanhamento: tenta de novo
                // mais devagar, porque quem está rodando é o governor e ele
                // segue trabalhando independente desta tela.
                agendar(5000);
            });
    }, [id]);

    useEffect(() => {
        if (!grafo) {
            return;
        }
        const temExecutando = grafo.nos.some(no => no.situacao === STEP_EXECUTANDO);
        if (temExecutando && timerRef.current === null) {
            timerRef.current = window.setTimeout(tail, 800);
        }
    }, [grafo, tail]);

    useEffect(() => () => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);


    const noSelecionado: InfraNo | null = grafo && selecionado
        ? grafo.nos.filter(no => no.chave === selecionado)[0] || null
        : null;

    /**
     * Roda um passo, resetando antes quando ele ja esta concluido.
     *
     * O backend recusa executar passo concluido ("use o reset para rodar de
     * novo"), e essa recusa e uma regra de consistencia, nao de interface: quem
     * decidiu rodar de novo ja disse o que queria. O reset entra aqui, sem virar
     * um botao separado que ninguem sabe o que faz.
     */
    /**
     * Liga ou desliga um ramo do deploy.
     *
     * Desligar nao apaga nada: o passo do ramo fora vira pulado, que ja conta
     * como concluido no resto do fluxo. Ligar de volta so devolve a pendente o
     * que nunca rodou.
     */
    const alternarRamo = (chave: string) => {
        if (!grafo || salvandoRamos || executando !== null) {
            return;
        }

        const atuais = grafo.ramos.filter(ramo => ramo.ligado).map(ramo => ramo.chave);
        let proximos: Array<string>;

        if (atuais.indexOf(chave) >= 0) {
            // Desligar arrasta quem depende: sem imagem nova, o refresh de uma
            // farm subiria instância da AMI antiga. O backend faz o caminho
            // contrário (ligar uma farm liga a imagem), mas aqui a intenção é
            // conhecida e vale resolver na hora, sem o clique parecer perdido.
            const cai = [chave];
            let mudou = true;
            while (mudou) {
                mudou = false;
                grafo.ramos.forEach(ramo => {
                    if (ramo.precisa && cai.indexOf(ramo.precisa) >= 0 && cai.indexOf(ramo.chave) < 0) {
                        cai.push(ramo.chave);
                        mudou = true;
                    }
                });
            }
            proximos = atuais.filter(outro => cai.indexOf(outro) < 0);
        } else {
            proximos = atuais.concat([chave]);
        }

        setSalvandoRamos(true);
        setErro(null);

        getApi().post('/infra/pipeline/' + id + '/ramos', { ramos: proximos })
            .then(resposta => setGrafo(resposta.data.grafo))
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui salvar os ramos.'))
            .finally(() => setSalvandoRamos(false));
    };

    /** Libera todas as etapas de uma vez, para quem quer o deploy completo. */
    const liberarTudo = () => {
        if (!grafo || salvandoRamos || executando !== null) {
            return;
        }

        setSalvandoRamos(true);
        setErro(null);

        getApi().post('/infra/pipeline/' + id + '/ramos', {
            ramos: grafo.ramos.map(ramo => ramo.chave)
        })
            .then(resposta => setGrafo(resposta.data.grafo))
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui liberar as etapas.'))
            .finally(() => setSalvandoRamos(false));
    };

    const executarPasso = (chave: string, resetarAntes = false, retomar = false) => {
        setExecutando(chave);
        setErro(null);
        setExecucaoIniciada(v => v + 1);

        const inicio = resetarAntes
            ? getApi().post('/infra/pipeline/' + id + '/step/' + chave + '/reset')
            : Promise.resolve(null);

        // Recarrega o histórico e o grafo enquanto o POST ainda está em curso:
        // é o que faz o subpasso aparecer acontecendo e o nó girar no DAG.
        const acompanhamento = window.setInterval(() => {
            setVersaoRuns(v => v + 1);
            getApi().get('/infra/pipeline/' + id + '/tail')
                .then(resposta => resposta.data.grafo && setGrafo(resposta.data.grafo))
                .catch(() => undefined);
        }, 1000);
        const pararAcompanhamento = () => window.clearInterval(acompanhamento);

        inicio
            .then(() => getApi().post('/infra/pipeline/' + id + '/step/' + chave + '/run',
                retomar ? { retomar: 1 } : {}))
            .then(resposta => {
                setGrafo(resposta.data.grafo);
                setVersaoRuns(v => v + 1);
            })
            .catch(motivo => {
                // O backend devolve o grafo junto com o erro justamente para o
                // log do passo que falhou aparecer aqui. Quando nem isso vem, o
                // estado já foi gravado no banco e basta recarregar.
                if (motivo?.response?.data?.grafo) {
                    setGrafo(motivo.response.data.grafo);
                } else {
                    carregar();
                }
                setErro(motivo?.response?.data?.error || 'O passo falhou.');
            })
            .finally(() => {
                pararAcompanhamento();
                setExecutando(null);
                // Solta o "iniciando" do historico mesmo quando o POST falhou:
                // sem isto o spinner ficava girando para sempre numa recusa.
                setVersaoRuns(v => v + 1);
            });
    };

    const resetarPasso = (chave: string) => {
        setExecutando(chave);
        getApi().post('/infra/pipeline/' + id + '/step/' + chave + '/reset')
            .then(resposta => { setGrafo(resposta.data.grafo); setVersaoRuns(v => v + 1); })
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui resetar o passo.'))
            .finally(() => setExecutando(null));
    };

    const confirmarReversao = (senha: string, esvaziar: boolean) => {
        if (!reverter) {
            return;
        }
        setRevertendo(true);
        setErroReversao(null);
        getApi().post('/infra/pipeline/' + id + '/step/' + reverter.chave + '/reverter', { senha, esvaziar })
            .then(resposta => {
                if (resposta.data.grafo) {
                    setGrafo(resposta.data.grafo);
                }
                setVersaoRuns(v => v + 1);
                setReverter(null);
                setErro(null);
            })
            .catch(motivo => {
                if (motivo?.response?.data?.grafo) {
                    setGrafo(motivo.response.data.grafo);
                }
                setErroReversao(motivo?.response?.data?.error || 'Não consegui reverter o passo.');
            })
            .finally(() => setRevertendo(false));
    };

    const executarLiberados = () => {
        setExecutando('__todos__');
        setErro(null);

        const acompanhamento = window.setInterval(() => {
            setVersaoRuns(v => v + 1);
            getApi().get('/infra/pipeline/' + id + '/tail')
                .then(resposta => resposta.data.grafo && setGrafo(resposta.data.grafo))
                .catch(() => undefined);
        }, 1500);

        getApi().post('/infra/pipeline/' + id + '/run')
            .then(resposta => setGrafo(resposta.data.grafo))
            .catch(motivo => {
                if (motivo?.response?.data?.grafo) {
                    setGrafo(motivo.response.data.grafo);
                }
                setErro(motivo?.response?.data?.error || 'A execução parou num passo com erro.');
            })
            .finally(() => { window.clearInterval(acompanhamento); setExecutando(null); });
    };

    if (carregando && !grafo) {
        return <div className="infra-carregando"><CSpinner color="primary" /></div>;
    }

    if (!grafo) {
        return (
            <div className="infra-container">
                <CAlert color="danger">{erro || 'Pipeline não encontrado.'}</CAlert>
            </div>
        );
    }

    const cabecalho = grafo.pipeline;

    // O desenho mostra o que este deploy vai fazer, e nao tudo que existe.
    //
    // Quem escolhe as etapas e a barra logo acima; desenhar tambem as
    // desligadas enchia a tela de card apagado e de linha de retorno que nao
    // levava a nada. Passo que ja rodou fica, mesmo se o ramo dele foi
    // desligado depois: aquilo e historico, nao plano.
    const nos = grafo.nos.filter(no => no.ligado !== false || no.tentativas > 0);
    const visivel: { [chave: string]: boolean } = {};
    nos.forEach(no => { visivel[no.chave] = true; });
    const arestasVisiveis = grafo.arestas.filter(
        aresta => visivel[aresta.de] && visivel[aresta.para]);
    const faixasVisiveis = grafo.faixas.filter(
        faixa => nos.some(no => no.faixa === faixa.chave));

    // Vizinhos do passo aberto, para as setas do modal. A ordem e a de leitura
    // e a lista e a dos visiveis: seta nao deve levar para passo que o desenho
    // nao mostra porque o ramo dele esta fora do deploy.
    const emOrdem = nos.slice().sort((a2, b2) => a2.numero - b2.numero);
    const ondeEstou = noSelecionado
        ? emOrdem.findIndex(no => no.chave === noSelecionado.chave)
        : -1;
    const passoAnterior = ondeEstou > 0 ? emOrdem[ondeEstou - 1] : null;
    const passoProximo = ondeEstou >= 0 && ondeEstou < emOrdem.length - 1
        ? emOrdem[ondeEstou + 1]
        : null;

    const concluidos = nos.filter(no => no.situacao === 3 || no.situacao === 5).length;
    const temLiberado = nos.some(no => no.liberado);
    // Producao nao tem cliente: slug, dominio e farm nao dizem nada ali, e nao
    // ha configuracao de loja para comparar.
    const ehProducao = cabecalho.tipo === TIPO_PRODUCAO;
    // Este deploy é o que está em andamento? Quem decide é o backend, que sabe
    // quais situações contam como encerrado. Vem no cabeçalho do pipeline, e
    // não na lista do histórico, então a resposta se atualiza a cada volta do
    // polling e não depende de abrir a gaveta.
    const esteEstaAberto = cabecalho.producaoAbertaId === cabecalho.id;

    return (
        <div className="infra-container infra-container-fluxo">
            <div className="infra-cabecalho">
                <div>
                    <h4 className="infra-titulo">
                        {ehProducao ? rotuloDeploy(cabecalho.id) : cabecalho.nome}
                        <CBadge color={corSituacaoPipeline(cabecalho.situacao)} className="ml-2">
                            {descricaoSituacaoPipeline(cabecalho.situacao)}
                        </CBadge>
                    </h4>
                    <div className="infra-subtitulo">
                        {!ehProducao && (
                            <>
                                <span>slug <strong>{cabecalho.slug}</strong></span>
                                <span>domínio <strong>{cabecalho.dominio}</strong></span>
                                <span>farm <strong>{cabecalho.farm}</strong></span>
                            </>
                        )}
                        <span>{concluidos} de {nos.length} passos</span>
                    </div>
                    {cabecalho.sandbox && (
                        <div className="infra-acessos">
                            <span>Acessos temporários:</span>
                            <a href={'https://' + cabecalho.sandbox} target="_blank" rel="noreferrer">
                                {cabecalho.sandbox}
                            </a>
                            <a href={'https://' + cabecalho.sandboxAdmin} target="_blank" rel="noreferrer">
                                {cabecalho.sandboxAdmin}
                            </a>
                        </div>
                    )}
                </div>
                <div className="infra-acoes-topo">
                    <CButton color="secondary" variant="outline" onClick={() => history.push('/infra')}>
                        Voltar
                    </CButton>
                    <CButton color="light" onClick={carregar} disabled={carregando}>
                        Atualizar
                    </CButton>
                    {!ehProducao && (
                        <CButton color="info" variant="outline" onClick={() => history.push('/infra/pipeline/' + id + '/config')}>
                            Configurações
                        </CButton>
                    )}
                    {ehProducao && (
                        <>
                            <CButton
                                color="light"
                                onClick={() => mostrandoHistorico
                                    ? setMostrandoHistorico(false)
                                    : abrirHistorico()}
                            >
                                Histórico
                            </CButton>
                            {esteEstaAberto
                                ? (
                                    <CButton
                                        color="light"
                                        onClick={() => setConfirmandoNovo(true)}
                                        disabled={trocandoDeploy || executando !== null}
                                    >
                                        Novo deploy
                                    </CButton>
                                )
                                : (
                                    <CButton
                                        color="warning"
                                        onClick={continuarDeploy}
                                        disabled={trocandoDeploy}
                                    >
                                        {trocandoDeploy
                                            ? 'Abrindo…'
                                            : cabecalho.producaoAbertaId
                                                ? 'Ir para o deploy atual'
                                                : 'Novo deploy'}
                                    </CButton>
                                )
                            }
                        </>
                    )}
                    <CButton
                        color="primary"
                        onClick={executarLiberados}
                        disabled={!temLiberado || executando !== null}
                    >
                        {executando === '__todos__' ? 'Executando…' : 'Executar'}
                    </CButton>
                </div>
            </div>

            {ehProducao && confirmandoNovo && (
                <div className="infra-card-confirma infra-confirma-fluxo">
                    <strong>Encerrar este deploy e começar do zero?</strong>
                    <p>
                        {concluidos > 0
                            ? 'Este deploy fica marcado como abandonado, com os ' + concluidos
                                + ' passos que já rodaram registrados no histórico. O que não rodou continua sem rodar.'
                            : 'Este deploy fica marcado como abandonado. Nenhum passo rodou nele, então não se perde trabalho.'}
                    </p>
                    <p className="infra-card-confirma-nota">
                        Isso encerra o registro, não o trabalho: passo que já saiu para a AWS ou
                        para uma máquina segue até o fim por conta própria.
                    </p>
                    <div className="infra-card-acoes">
                        <CButton color="danger" onClick={comecarNovoDeploy} disabled={trocandoDeploy}>
                            {trocandoDeploy ? 'Criando…' : 'Encerrar e criar novo'}
                        </CButton>
                        <CButton color="light" onClick={() => setConfirmandoNovo(false)} disabled={trocandoDeploy}>
                            Cancelar
                        </CButton>
                    </div>
                </div>
            )}

            {/* Gaveta na direita, e não um painel empurrando o fluxo para
                baixo. Clicar numa linha troca o deploy atrás dela e a gaveta
                fica onde está, para dar para passar de um deploy a outro
                comparando. Quem fecha é o clique na cortina escura de fora. */}
            {ehProducao && mostrandoHistorico && (
                <>
                    <div
                        className="infra-gaveta-cortina"
                        onClick={() => setMostrandoHistorico(false)}
                    />
                    <aside className="infra-gaveta">
                        <div className="infra-gaveta-titulo">
                            <span>Deploys de produção</span>
                            <CButton color="light" size="sm" onClick={() => setMostrandoHistorico(false)}>
                                fechar
                            </CButton>
                        </div>

                        <div className="infra-gaveta-corpo">
                            {!historico && (
                                <div className="infra-gaveta-vazio">
                                    {carregandoHistorico ? 'Carregando…' : 'Não consegui carregar a lista.'}
                                </div>
                            )}
                            {historico && historico.rows.length === 0 && (
                                <div className="infra-gaveta-vazio">Nenhum deploy ainda.</div>
                            )}
                            {(historico?.rows || []).map(linha => (
                                <button
                                    type="button"
                                    key={linha.id}
                                    className={'infra-gaveta-linha'
                                        + (linha.id === Number(id) ? ' infra-gaveta-atual' : '')}
                                    onClick={() => irParaDeploy(linha.id)}
                                    title={'#' + linha.id
                                        + (linha.criacao ? ' de ' + linha.criacao : '')
                                        + (linha.usuario ? ', aberto por ' + linha.usuario : '')}
                                >
                                    <span className="infra-gaveta-linha-topo">
                                        <strong>#{linha.id}</strong>
                                        {linha.id === cabecalho.producaoAbertaId && (
                                            <span className="infra-gaveta-marca">em andamento</span>
                                        )}
                                        <CBadge color={corSituacaoPipeline(linha.situacao)}>
                                            {descricaoSituacaoPipeline(linha.situacao)}
                                        </CBadge>
                                    </span>
                                    <span className="infra-gaveta-linha-pe">
                                        <span>
                                            {linha.criacao}
                                            {' · '}{linha.concluidos} de {linha.total} passos
                                            {linha.erros > 0 && ' · ' + linha.erros + ' com erro'}
                                        </span>
                                        {/* Nome à direita, cortado com reticências se não
                                            couber. A gaveta é estreita, e é o quando e o
                                            quanto que se lê primeiro. O title do botão traz
                                            a linha inteira. */}
                                        {linha.usuario && (
                                            <span className="infra-gaveta-quem">{linha.usuario}</span>
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {historico && historico.paginas > 1 && (
                            <div className="infra-gaveta-paginas">
                                <span>
                                    {(historico.pagina - 1) * historico.porPagina + 1}
                                    {' a '}
                                    {Math.min(historico.pagina * historico.porPagina, historico.total)}
                                    {' de '}{historico.total}
                                </span>
                                <div className="infra-gaveta-botoes">
                                    <CButton
                                        color="light"
                                        size="sm"
                                        onClick={() => carregarHistorico(historico.pagina - 1)}
                                        disabled={historico.pagina <= 1 || carregandoHistorico}
                                    >
                                        ‹
                                    </CButton>
                                    <span className="infra-gaveta-pagina-atual">
                                        {carregandoHistorico
                                            ? '…'
                                            : historico.pagina + ' / ' + historico.paginas}
                                    </span>
                                    <CButton
                                        color="light"
                                        size="sm"
                                        onClick={() => carregarHistorico(historico.pagina + 1)}
                                        disabled={historico.pagina >= historico.paginas || carregandoHistorico}
                                    >
                                        ›
                                    </CButton>
                                </div>
                            </div>
                        )}
                    </aside>
                </>
            )}

            {aviso && (
                <CAlert color="warning" onClick={() => setAviso(null)}>{aviso}</CAlert>
            )}

            {grafo.ramos.length > 0 && (
                // Uma linha so, aproveitando a largura. Quatro linhas
                // empilhadas comiam a altura da tela e espremiam o fluxo, que
                // e o que se veio olhar.
                <div className="infra-etapas">
                    {([
                        { chave: 'imagem', rotulo: 'Imagem' },
                        { chave: 'front',  rotulo: 'Front' },
                        { chave: 'apis',   rotulo: 'APIs' },
                        { chave: 'jobs',   rotulo: 'Jobs' },
                        { chave: 'admins', rotulo: 'Admins' }
                    ] as Array<{ chave: SecaoDoRamo; rotulo: string }>)
                        .map(linha => {
                            const daLinha = grafo.ramos.filter(
                                ramo => linhaDoRamo(ramo.chave) === linha.chave);
                            if (!daLinha.length) {
                                return null;
                            }

                            return (
                                <span className="infra-etapas-grupo" key={linha.chave}>
                                    <span className="infra-etapas-titulo">{linha.rotulo}</span>
                                    <span className="infra-etapas-opcoes">
                                    {daLinha.map(ramo => (
                                        <button
                                            key={ramo.chave}
                                            type="button"
                                            className={'infra-ramo-chip'
                                                + (ramo.ligado ? ' infra-ramo-ligado' : '')}
                                            onClick={() => alternarRamo(ramo.chave)}
                                            disabled={salvandoRamos || executando !== null}
                                            title={'conta ' + ramo.conta + ' · ' + ramo.regiao}
                                        >
                                            <span className="infra-ramo-marca">
                                                {ramo.ligado ? '✓' : '○'}
                                            </span>
                                            {ramo.rotulo}
                                        </button>
                                    ))}
                                    </span>
                                </span>
                            );
                        })}

                    {/* Encostado à direita: é atalho, não um grupo de opções,
                        e assim ele não disputa espaço com os chips. */}
                    <span className="infra-etapas-grupo infra-etapas-atalho">
                        <span className="infra-etapas-opcoes">
                        <button
                            type="button"
                            className="infra-ramo-chip infra-ramo-tudo"
                            onClick={liberarTudo}
                            disabled={salvandoRamos || executando !== null
                                || grafo.ramos.every(ramo => ramo.ligado)}
                        >
                            liberar tudo
                        </button>
                        </span>
                    </span>
                </div>
            )}

            <FlowGraph
                nos={nos}
                arestas={arestasVisiveis}
                faixas={faixasVisiveis}
                selecionado={selecionado}
                onSelecionar={setSelecionado}
            />

            <StepModal
                no={noSelecionado}
                pipelineId={id}
                versaoRuns={versaoRuns}
                execucaoIniciada={execucaoIniciada}
                executando={executando}
                onFechar={() => setSelecionado(null)}
                onExecutar={executarPasso}
                onResetar={resetarPasso}
                onReverter={no => { setErroReversao(null); setReverter(no); }}
                anterior={passoAnterior}
                proximo={passoProximo}
                onIr={setSelecionado}
            />

            <ReverterModal
                aberto={reverter !== null}
                rotulo={reverter ? reverter.rotulo : ''}
                aviso={reverter ? reverter.avisoReversao : null}
                erro={erroReversao}
                revertendo={revertendo}
                onFechar={() => { setReverter(null); setErroReversao(null); }}
                onConfirmar={confirmarReversao}
            />
        </div>
    );
};

export default PipelineFlow;
